/*
  Carter & Me — the wishlist page.

  cm-wishlist.js owns the heart and the storage; this owns the page that
  renders what was saved. Kept separate so the heart still works on every
  other page without carrying this fetching logic around.

  Cards are fetched as rendered Liquid through the wishlist-card alternate
  product template, not rebuilt from product JSON. The card markup lives
  in one place and this page inherits every future change to it.

  A saved handle can stop resolving: the piece was unpublished, deleted,
  or the handle changed. That returns a non-OK response, and the handle is
  pruned from storage rather than left to fail on every future visit.
*/

(function () {
  const KEY = 'cm:wishlist';

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(handles) {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (e) {
      /* Private mode. The page still reflects this view. */
    }
  }

  function prune(handle) {
    const next = read().filter((h) => h !== handle);
    write(next);
    return next;
  }

  function setup(root) {
    const grid = root.querySelector('[data-cm-wl-grid]');
    const empty = root.querySelector('[data-cm-wl-empty]');
    const countEl = root.querySelector('[data-cm-wl-count]');
    const status = root.querySelector('[data-cm-wl-status]');
    if (!grid) return;

    const one = countEl && countEl.dataset.one ? countEl.dataset.one : 'piece saved';
    const many = countEl && countEl.dataset.many ? countEl.dataset.many : 'pieces saved';

    // handle -> card element. Lets a change event remove or add just the
    // cards that actually changed instead of refetching the whole grid.
    const cards = new Map();
    const inFlight = new Set();

    // Cards are appended as their fetches land, which is completion order,
    // not saved order. Re-append in saved order so the grid is stable
    // between visits instead of reshuffling on every load. appendChild
    // moves an existing node, so this is a reorder, not a rebuild.
    function reorder() {
      read().forEach((handle) => {
        const el = cards.get(handle);
        if (el) grid.appendChild(el);
      });
    }

    function paint() {
      reorder();
      const n = cards.size;
      if (countEl) countEl.textContent = n ? `${n} ${n === 1 ? one : many}` : '';
      // Only claim empty once nothing is still arriving, or the empty
      // state flashes on every load before the first card lands.
      const settled = inFlight.size === 0;
      if (empty) empty.hidden = !(settled && n === 0);
      grid.hidden = n === 0;
      if (status && settled) status.textContent = n ? `${n} ${n === 1 ? one : many}` : '';
    }

    async function add(handle) {
      if (cards.has(handle) || inFlight.has(handle)) return;
      inFlight.add(handle);
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?view=wishlist-card`);
        if (!res.ok) throw new Error(String(res.status));

        const html = (await res.text()).trim();
        if (!html) throw new Error('empty');

        // Still wanted? The visitor may have unhearted it mid-fetch.
        if (!read().includes(handle)) return;

        const wrap = document.createElement('div');
        wrap.className = 'cm-wl__item';
        wrap.dataset.handle = handle;
        wrap.innerHTML = html;
        grid.appendChild(wrap);
        cards.set(handle, wrap);

        // The freshly injected heart needs its pressed state. Call the
        // sync directly rather than dispatching cm:wishlist:change: the
        // list did not change, and the event is what this page listens
        // to, so firing it here would re-enter sync once per card.
        if (typeof window.cmWishlistSync === 'function') window.cmWishlistSync(wrap);
      } catch (e) {
        // Unresolvable handle: drop it so it stops costing a request.
        console.warn('[cm-wishlist-page] dropping unresolvable handle', handle, e);
        prune(handle);
      } finally {
        inFlight.delete(handle);
        paint();
      }
    }

    function sync() {
      const saved = read();

      // Remove what is no longer saved.
      cards.forEach((el, handle) => {
        if (!saved.includes(handle)) {
          el.remove();
          cards.delete(handle);
        }
      });

      paint();
      saved.forEach(add);
    }

    document.addEventListener('cm:wishlist:change', sync);
    sync();
  }

  function init() {
    document.querySelectorAll('[data-cm-wl]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
